/**
 * 我的命盤頁（tianyun-chart LIFF）。
 *
 * SPEC § 6.3 頂部三張並排迷你盤 + 中部 L1 自我層解讀 + 底部 CTA。
 *
 * Phase 1 先鋪版面 + 打 /me 顯示 profile，三張迷你盤吃 placeholder 值；
 * Week 10 QA 之前接 worker-astro /compute/natal 填真命盤。
 */
import { useEffect, useState } from 'react';

import { BaziMiniChart } from '../components/BaziMiniChart';
import { WesternMiniChart } from '../components/WesternMiniChart';
import { ZiweiMiniChart } from '../components/ZiweiMiniChart';
import { getMe, type MeResponse, ApiError } from '../lib/api';
import { useLiff } from '../lib/liff';

interface ChartPageProps {
  liffId: string | undefined;
}

export function ChartPage({ liffId }: ChartPageProps) {
  const liff = useLiff(liffId);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (liff.kind !== 'ready') return;
    setLoading(true);
    getMe(liff.idToken)
      .then((r) => {
        setMe(r);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setError('還沒完成 LINE 加好友 onboarding，請先到 LINE bot 完成生辰登記。');
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => setLoading(false));
  }, [liff]);

  if (liff.kind === 'loading') {
    return <Loading label="連接 LINE⋯" />;
  }
  if (liff.kind === 'not-logged-in') {
    return (
      <main className="flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <p className="text-sm text-ink/70">請先登入 LINE</p>
          <button
            onClick={liff.login}
            className="mt-4 rounded-full bg-accent px-6 py-2 text-sm font-medium text-cream"
          >
            登入
          </button>
        </div>
      </main>
    );
  }
  if (liff.kind === 'error') {
    return <ErrorPane message={liff.message} />;
  }

  return (
    <main className="mx-auto min-h-screen max-w-md p-4 pb-10">
      <header className="mb-4">
        <h1 className="font-serif text-2xl font-bold">我的命盤</h1>
        <p className="mt-1 text-xs text-ink/60">
          八字 · 紫微 · 西洋三軌疊加（Phase 1 先顯示骨架，完整命盤之後推送）
        </p>
      </header>

      {loading && <Loading label="讀取命盤⋯" />}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* 三張迷你盤 */}
      <section className="mb-6 grid grid-cols-1 gap-3">
        <BaziMiniChart />
        <ZiweiMiniChart />
        <WesternMiniChart />
      </section>

      {/* L1 自我層解讀（MVP 佔位） */}
      <section className="mb-6 rounded-lg border border-paper bg-cream/60 p-4">
        <h2 className="mb-2 font-serif text-base font-bold">自我層解讀</h2>
        <p className="text-xs leading-relaxed text-ink/70">
          三軌融合的個人特質、強項、盲點將在 Week 10 上線前填入。
          目前先提供生辰確認與每日推送訂閱。
        </p>
      </section>

      {/* Profile 確認 */}
      {me && (
        <section className="mb-6 rounded-lg border border-paper bg-cream/60 p-4">
          <h2 className="mb-2 font-serif text-base font-bold">基本資料</h2>
          <dl className="space-y-1 text-xs text-ink/75">
            <Row label="生日" value={me.profile.birthDate} />
            <Row label="時辰" value={me.profile.birthTime} />
            <Row label="時區" value={me.profile.timezone} />
            <Row
              label="性別"
              value={me.profile.gender === 'male' ? '男' : me.profile.gender === 'female' ? '女' : null}
            />
            <Row label="Email" value={me.profile.email} />
          </dl>
          <a
            href="/profile"
            className="mt-3 inline-block text-xs font-medium text-accent underline"
          >
            編輯個資 →
          </a>
        </section>
      )}

      {/* CTA 導到 EMAIL 完整版 */}
      <section className="text-center">
        <a
          href="https://tianyun.app/subscribe"
          target="_blank"
          rel="noreferrer noopener"
          className="inline-block rounded-full bg-accent px-8 py-3 text-sm font-medium text-cream shadow"
        >
          解鎖每日完整版
        </a>
      </section>
    </main>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-sm text-ink/60">
      {label}
    </div>
  );
}

function ErrorPane({ message }: { message: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-6 text-center">
      <div>
        <h1 className="font-serif text-lg font-bold">無法載入</h1>
        <p className="mt-2 text-xs text-ink/70 break-words">{message}</p>
      </div>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-ink/50">{label}</dt>
      <dd className="font-medium">{value ?? '—'}</dd>
    </div>
  );
}
